import { useGetSuperAdminFranchisesFranchiseIdUsage } from '@agos/api-client/platform'
import {
  T,
  USAGE_PRESETS,
  WHOLE_DEFAULT_WINDOW,
  formatCents,
  formatCount,
  reportWindowParams,
  tt,
  type ReportWindow,
} from '@agos/shared'
import { useMemo, useState } from 'react'
import { useParams } from 'wouter'
import { PageHeader, ReadFailed, ReportWindowBar } from '@agos/ui'
import { Gauge } from 'lucide-react'

import {
  amountCents,
  amountLabel,
  callTotals,
  costSummary,
  dailyTotals,
  dayBarPercent,
  dayLabel,
  daySegments,
  failureNote,
  failureRate,
  isFailing,
  operationsOf,
  passThroughCount,
  serviceLabel,
  serviceTint,
  servicesInDaily,
  totalUnits,
  usageOf,
  windowLabel,
} from './usage'

/**
 * What one franchise consumed from the platform's third-party services.
 *
 * The money column is **two different things** and says which on each row: `tatrapay` carries the
 * volume that passed through (8 373,05 € in the measured month) while `claude_translate` carries what
 * the platform was charged (0,17 €). Summing the column as one figure would report the month as costing
 * eight thousand euros.
 *
 * **Three of the five keys the server sends were being dropped** - `SAAS.md §3.51`. `window`,
 * `daily` and `byOperation` were all answered and none was drawn, so the screen showed a list of
 * service totals over an unstated period with no indication of what had been called or when. The
 * services were also labelled with their wire keys: an operator was reading `claude_translate`
 * where legacy says *AI prekladač (Claude)*.
 */
export function FranchiseUsagePage() {
  const params = useParams<{ id: string }>()
  const franchiseId = Number(params.id ?? 0)
  // §3.84 - the window is no longer *left to the server's default*, which is what the comment here
  // used to say and what made this screen answer one question. Legacy offers seven presets, out to
  // *Minulý rok*: API spend is the figure an operator compares across years.
  const [window, setWindow] = useState<ReportWindow>(WHOLE_DEFAULT_WINDOW)
  const { data, isLoading, isError } = useGetSuperAdminFranchisesFranchiseIdUsage(
    franchiseId,
    reportWindowParams(window),
    { query: { enabled: Number.isFinite(franchiseId) && franchiseId > 0 } },
  )
  const report = useMemo(() => usageOf(data), [data])
  const totals = callTotals(report)
  // Not `useMemo`: the React Compiler refuses to optimise a component whose manual memoization it
  // cannot preserve, and `report` is already memoized above. Calling directly lets it own both.
  const days = dailyTotals(report)
  const period = windowLabel(report)

  return (
    <main className="space-y-3" data-usage>
      <PageHeader
        title={tt('Spotreba API')}
        description={tt('Volania platených služieb za obdobie, a čo stáli.')}
        icon={<Gauge className="w-5 h-5" />}
      />

      <ReportWindowBar
        window={window}
        onChange={setWindow}
        presets={USAGE_PRESETS}
        defaultNote={period}
      />
      <div className="rounded-2xl border border-border p-3" data-usage-summary>
        <p className="text-sm font-bold">{report.franchiseName}</p>
        <p className="text-xs" data-usage-calls>
          {formatCount(totals.calls)} <T>volaní</T> &middot; {formatCount(totals.failures)} <T>chýb</T>
        </p>
        {/* §3.111 - legacy's second KPI, and it is a different question from calls: one Resend call
            sends one e-mail and one BulkGate call may send several SMS, so units is what the platform
            is billed on. Drawn only when there are any, because most services report none. */}
        {totalUnits(report) > 0 && (
          <p className="text-xs" data-usage-units-total>
            {formatCount(totalUnits(report))} <T>jednotiek (SMS, e-maily, atď.)</T>
          </p>
        )}
        <p className="text-[11px] text-muted-foreground" data-usage-cost>
          {costSummary(report)}
        </p>
        {/* §3.111 - the count beside the money. `costSummary` says 8 373,05 € passed through; that is
            200 payments or 2, and which of those it is changes what the figure means. */}
        {passThroughCount(report) > 0 && (
          <p className="text-[11px] text-muted-foreground" data-usage-payments>
            {formatCount(passThroughCount(report))} <T>úspešných platieb kartou</T>
          </p>
        )}
        {/* The period every figure above is over. It was answered by the server and never shown, so
            the numbers were unreadable: 26 203 calls is a different story over a day and a month. */}
        {period !== '' && (
          <p className="mt-1 text-[11px] text-muted-foreground" data-usage-window>
            <T>Za obdobie</T> {period}
          </p>
        )}
      </div>

      {/* An explicit nothing, rather than a page that just stops. A franchise with no third-party
          traffic is a normal state and the operator has to be able to tell it from a failed load. */}
      {/* §3.107 - §3.82's banner, in the directory its sweep never reached. */}
      {isError && (
        <ReadFailed marker="usage-error"><T>Spotrebu sa nepodarilo načítať.</T></ReadFailed>
      )}
      {!isLoading && !isError && report.totals.length === 0 && (
        <p className="rounded-2xl border border-dashed border-border p-4 text-xs text-muted-foreground" data-usage-empty>
          <T>Za toto obdobie neboli zaznamenané žiadne volania služieb.</T>
        </p>
      )}

      {/* Calls per day, summed across services. Drawn with divs, as `StatisticsPage`'s heat grid is,
          rather than pulling in a chart library for one screen. */}
      {days.length > 1 && (
        <div className="rounded-2xl border border-border p-3" data-usage-daily>
          <p className="mb-2 text-xs font-semibold">
            <T>Volania po dňoch</T>
          </p>
          {/* §3.111 - split by service, which is what legacy's chart does and what makes it a chart.
              A single colour says *the 14th was busy*; this says *the 14th was busy with Google Maps*,
              and on the measured month one service accounts for 26 203 calls against the next at 185 -
              so an unsplit bar draws one service and hides the rest. `flex-col-reverse` stacks the
              biggest at the bottom, as legacy's does. */}
          <div className="flex items-end gap-[3px]" style={{ height: '64px' }}>
            {days.map((day) => (
              <div
                key={day.day}
                className="flex flex-1 flex-col-reverse overflow-hidden rounded-t bg-muted"
                style={{ height: `${dayBarPercent(report, day)}%`, minWidth: '3px' }}
                title={`${dayLabel(day.day)} · ${formatCount(day.calls)}`}
                data-usage-day={day.day}
                data-usage-day-calls={day.calls}
              >
                {daySegments(report, day.day).map((segment) => (
                  <div
                    key={segment.service}
                    className={serviceTint(segment.service)}
                    style={{ height: `${segment.percent}%` }}
                    title={`${serviceLabel(segment.service)}: ${formatCount(segment.calls)}`}
                    data-usage-day-service={segment.service}
                  />
                ))}
              </div>
            ))}
          </div>
          {/* `.at()` rather than `[0]`: `noUncheckedIndexedAccess` is on and the `length > 1` guard
              above does not narrow an index, which the build caught after vitest passed. */}
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>{dayLabel(days.at(0)?.day ?? '')}</span>
            <span>{dayLabel(days.at(-1)?.day ?? '')}</span>
          </div>
          {/* Without this the colours are decoration. Read off `daily` rather than `totals`, so the
              legend names exactly the services the bars above contain. */}
          <div className="mt-2 flex flex-wrap gap-2 text-[10px]" data-usage-legend>
            {servicesInDaily(report).map((service) => (
              <span key={service} className="inline-flex items-center gap-1" data-usage-legend-service={service}>
                <span className={`inline-block h-2 w-2 rounded-sm ${serviceTint(service)}`} />
                <T>{serviceLabel(service)}</T>
              </span>
            ))}
          </div>
        </div>
      )}

      {report.totals.map((usage) => (
        <div
          key={usage.service}
          className="rounded-xl border border-border p-2 text-xs"
          data-usage-service={usage.service}
          data-usage-failing={isFailing(usage) ? 'yes' : 'no'}
        >
          {/* The label, with the key kept beside it: the operator reads the name, and anybody
              comparing this against `api_usage_log` or the pricing screen needs the key. */}
          <p className="font-semibold">
            <span data-usage-service-label>
              <T>{serviceLabel(usage.service)}</T>
            </span>{' '}
            &middot;{' '}
            {formatCount(usage.calls)}
          </p>
          {serviceLabel(usage.service) !== usage.service && (
            <p className="text-[10px] text-muted-foreground" data-usage-service-key>
              {usage.service}
            </p>
          )}
          {failureNote(usage) !== '' && (
            <p className={`text-[10px] ${isFailing(usage) ? 'text-red-500' : 'text-muted-foreground'}`} data-usage-failures>
              {failureNote(usage)} &middot; {formatCount(Math.round(failureRate(usage) * 10) / 10)} %
            </p>
          )}
          {usage.units > 0 && (
            <p className="text-[10px] text-muted-foreground" data-usage-units>
              {formatCount(usage.units)} <T>jednotiek</T>
            </p>
          )}
          {amountLabel(usage) !== '' && (
            <p className="text-[10px]" data-usage-amount={usage.service}>
              {amountLabel(usage)}: {formatCents(amountCents(usage))}
            </p>
          )}

          {/* What was actually called. The server has always sent this and no screen drew it, so
              "26 203 Google Maps calls" could not be told apart from a runaway loop. */}
          {operationsOf(report, usage.service).length > 0 && (
            <table className="mt-2 w-full text-[10px]" data-usage-operations={usage.service}>
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left font-normal">
                    <T>Operácia</T>
                  </th>
                  <th className="text-right font-normal">
                    <T>Volaní</T>
                  </th>
                  {/* §3.111 - legacy's operation table has this column and the server has always sent
                      it. One call is not one SMS, so *which operation is sending them* is unanswerable
                      without it. */}
                  <th className="text-right font-normal">
                    <T>Jednotiek</T>
                  </th>
                  <th className="text-right font-normal">
                    <T>Chýb</T>
                  </th>
                </tr>
              </thead>
              <tbody>
                {operationsOf(report, usage.service).map((operation) => (
                  <tr key={`${operation.service}-${operation.operation}`} data-usage-operation={operation.operation}>
                    <td className="truncate">{operation.operation === '' ? '-' : operation.operation}</td>
                    <td className="text-right">{formatCount(operation.calls)}</td>
                    <td className="text-right" data-usage-operation-units={operation.units}>
                      {operation.units === 0 ? '-' : formatCount(operation.units)}
                    </td>
                    <td className={`text-right ${operation.failure > 0 ? 'text-red-500' : ''}`}>
                      {operation.failure === 0 ? '-' : formatCount(operation.failure)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </main>
  )
}
